import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const authHeader = (): Record<string, string> => {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Consulta do RASTREADOR (at-75, ADR 0033) — "este veículo está na oficina?".
 *
 * A resposta é deliberadamente estreita: nada de esteira, prazo, liberação ou observação
 * interna. Quem precisar de mais que isto está na tela errada, não faltando campo aqui.
 *
 * ⚠️ Esta função NUNCA engole erro (at-60). Quem chama precisa distinguir "a oficina não
 * tem este veículo" de "não consegui perguntar" — as duas coisas produzem lista vazia, e
 * confundi-las faz o rastreador ligar para o associado, que é a dor que a tela existe
 * para evitar.
 */
export interface VeiculoRastreado {
  id: string;
  plate: string;
  model: string | null;
  /** Rótulo de exibição já traduzido pelo backend ("Associado"), não o enum. */
  carClass: string | null;
  ownerName: string | null;
  ownerPhone: string | null;
  entryDate: string | null;
  /** `true` quando o atendimento já foi encerrado (só aparece na busca por placa). */
  entregue: boolean;
  /** Data da entrega quando `entregue`; `null` para quem está na oficina agora. */
  saiuEm: string | null;
}

export interface RespostaRastreamento {
  total: number;
  page: number;
  limit: number;
  itens: VeiculoRastreado[];
}

export const buscarVeiculosRastreamento = async (params: {
  busca?: string;
  page?: number;
  limit?: number;
}): Promise<RespostaRastreamento> => {
  const { data } = await axios.get<RespostaRastreamento>(`${BACKEND_URL}/rastreamento/veiculos`, {
    params: {
      ...(params.busca ? { busca: params.busca } : {}),
      page: params.page ?? 1,
      limit: params.limit ?? 20,
    },
    headers: authHeader(),
  });

  return data;
};

/**
 * Posição do veículo na rastreadora externa (at-76, ADR 0034).
 *
 * ⚠️ Duas funções porque são duas coisas diferentes, e confundi-las tem consequência real:
 * `obterPosicaoGravada` apenas LÊ o que já foi consultado (é o que abre com o modal), e
 * `consultarPosicaoAgora` FALA com a rastreadora. Chamar a segunda ao abrir a tela
 * transformaria cada clique numa consulta de geolocalização do associado.
 *
 * Como o resto deste arquivo, nenhuma das duas engole erro (at-60): "sem posição" e "não
 * consegui consultar" levam a ações opostas de quem está na tela.
 */
export interface PosicaoRastreador {
  imei?: string;
  placa?: string;
  chassi?: string;
  ignicaoLigada?: string;
  voltagemBateria?: string;
  movimento?: string;
  bloqueado?: string;
  statusGPRS?: string;
  dataGPRS?: string;
  statusGPS?: string;
  dataGPS?: string;
  satelites?: string;
  velocidade?: string;
  latitude?: string;
  longitude?: string;
  /** Endereço por extenso — é a informação que o rastreador de fato quer ler. */
  endereco?: string;
  /** Link do Google Maps montado pela própria rastreadora. */
  google?: string;
}

export interface ConsultaPosicao {
  /** `null` quando ninguém consultou este atendimento ainda. */
  consultadoEm: string | null;
  consultadoPorNome: string | null;
  posicao: PosicaoRastreador | null;
  /** `false` quando a rastreadora não está configurada no ambiente — a tela esconde o botão. */
  integracaoConfigurada: boolean;
  /**
   * `false` quando o veículo não tem o IMEI do rastreador cadastrado.
   *
   * ⚠️ Desde a at-81 isto **não desabilita mais o botão**: a busca por placa passou a
   * funcionar na rastreadora (medido em 21/08/2026, depois de o fornecedor corrigir a rota),
   * e o backend descobre e grava o IMEI sozinho no primeiro clique. O campo serve ao cadastro
   * inline (ADR 0035) e à transição `false → true`, que é como a tela avisa que a descoberta
   * automática funcionou.
   */
  possuiImei: boolean;
  /** O IMEI atual, para a edição inline do modal (ADR 0035). `null` = ainda não cadastrado. */
  rastreadorImei: string | null;
  /**
   * `true` quando a placa devolvida pela rastreadora **não é** a deste atendimento (at-81).
   *
   * Existe porque um IMEI digitado errado exibiria, em silêncio, a posição de outro veículo
   * (`BUG-V91`). Não esconde a posição — só avisa, e o aviso persiste ao reabrir o modal.
   */
  placaDivergente: boolean;
  /**
   * `false` quando o atendimento não tem documento do contrato de rastreamento gravado (at-83).
   *
   * Nesse caso a consulta usa a cascata padrão: CNPJ da empresa e, se o evento for reconhecido
   * pela Hinova, o CPF do associado no SGA.
   */
  possuiCpfCnpj: boolean;
  /**
   * O documento gravado, para a edição inline (ADR 0037).
   *
   * ⚠️ É o do **cliente do contrato de rastreamento**, que nem sempre é o proprietário do
   * veículo: medido em 24/08/2026, `KVV9G43` está sob o CNPJ da empresa e `KRX9355` sob o CPF
   * do associado.
   */
  rastreadorCpfCnpj: string | null;
}

/**
 * O que o backend informa quando NENHUM documento conhecido localizou o veículo (at-83).
 *
 * A lista é de RÓTULOS ("CNPJ da empresa"), nunca de valores — ela existe para o rastreador
 * entender que o sistema já tentou o que sabia, e que o próximo passo é dele: informar o
 * documento do contrato. Sem ela, a falha é indistinguível do erro genérico anterior.
 */
export interface ErroDeConsultaPosicao {
  message?: string;
  origem?: 'OFICINA' | 'RASTREADORA';
  causa?: string;
  documentosTentados?: string[];
}

/** Lê a última consulta GRAVADA. Não fala com a rastreadora. */
export const obterPosicaoGravada = async (carId: string): Promise<ConsultaPosicao> => {
  const { data } = await axios.get<ConsultaPosicao>(
    `${BACKEND_URL}/rastreamento/veiculos/${carId}/posicao`,
    { headers: authHeader() },
  );

  return data;
};

/** Consulta a rastreadora AO VIVO e grava o resultado como última consulta. */
export const consultarPosicaoAgora = async (carId: string): Promise<ConsultaPosicao> => {
  const { data } = await axios.post<ConsultaPosicao>(
    `${BACKEND_URL}/rastreamento/veiculos/${carId}/posicao/consultar`,
    {},
    { headers: authHeader() },
  );

  return data;
};

/**
 * Cadastra/edita o IMEI do rastreador (ADR 0035) — a primeira escrita do papel, e restrita a
 * este campo. A alteração é auditada no backend (quem, quando, de/para); a resposta é a
 * consulta gravada já atualizada, pronta para substituir o estado do modal.
 */
export const salvarImeiRastreador = async (carId: string, imei: string): Promise<ConsultaPosicao> => {
  const { data } = await axios.patch<ConsultaPosicao>(
    `${BACKEND_URL}/rastreamento/veiculos/${carId}/imei`,
    { imei },
    { headers: authHeader() },
  );

  return data;
};

/**
 * Cadastra/edita o CPF/CNPJ do contrato de rastreamento (at-83, ADR 0037).
 *
 * É o ESCAPE da cascata de descoberta: quando nem o documento gravado, nem o CNPJ da empresa,
 * nem o CPF do associado no SGA localizam o equipamento, este é o único caminho. Medido em
 * 24/08/2026, ele é a MAIORIA dos casos, não a exceção: 21 dos 39 atendimentos ativos têm
 * número de evento que a Hinova não reconhece, então nem o passo do SGA os alcança.
 */
export const salvarCpfCnpjRastreador = async (
  carId: string,
  cpfCnpjCliente: string,
): Promise<ConsultaPosicao> => {
  const { data } = await axios.patch<ConsultaPosicao>(
    `${BACKEND_URL}/rastreamento/veiculos/${carId}/cpf-cnpj`,
    { cpfCnpjCliente },
    { headers: authHeader() },
  );

  return data;
};
